// ============================================================================
// NewPostingDialog — modal « Demande d'affichage » (E1d, F-303/F-304)
// ============================================================================
// Portage maison du PoC new-posting-dialog.tsx : modal locale (tokens DS, gabarit
// overlay NewCommitteeDialog E3a — pas de dependance base-ui) + mutation
// useCreatePosting. Champs M-300 : equipe (selecteur si >1, sinon fixe), titre,
// description, taches (max 3), echeance + LOT A : diffusion / publication externe
// / nombre de postes (cablage MINIMAL — refonte visuelle = LOT B). Visible si
// writableTeams.length>0 (garde serveur re-verifiee).

import { useState } from 'react';
import { X } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useCreatePosting, postingErrorCode, type TeamMini,
  type PostingVisibility, type PostingExternalStatus,
} from '../../hooks/usePostings';
import { TasksFields } from './TasksFields';


export function NewPostingDialog({
  open,
  onClose,
  onError,
  writableTeams,
}: {
  open: boolean;
  onClose: () => void;
  /** Remonte le code court d'erreur a la page (alerte ?error= — F-301). */
  onError: (code: string) => void;
  writableTeams: TeamMini[];
}) {
  const { t } = useTranslation();
  const create = useCreatePosting();

  const singleTeam = writableTeams.length === 1 ? writableTeams[0] : null;
  const [teamId, setTeamId] = useState(singleTeam?.id ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tasks, setTasks] = useState<string[]>(['']);
  const [deadline, setDeadline] = useState('');
  const [visibility, setVisibility] = useState<PostingVisibility>('internal');
  const [externalStatus, setExternalStatus] = useState<PostingExternalStatus | ''>('');
  const [externalUrl, setExternalUrl] = useState('');
  const [positionsCount, setPositionsCount] = useState(1);
  const [stepError, setStepError] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setTeamId(singleTeam?.id ?? '');
    setTitle('');
    setDescription('');
    setTasks(['']);
    setDeadline('');
    setVisibility('internal');
    setExternalStatus('');
    setExternalUrl('');
    setPositionsCount(1);
    setStepError(null);
  }

  function close() {
    reset();
    onClose();
  }

  function submit() {
    const selectedTeam = singleTeam?.id ?? teamId;
    if (!selectedTeam) {
      setStepError(t('gest:affichages.form.teamRequired'));
      return;
    }
    if (!title.trim()) {
      setStepError(t('gest:affichages.form.titleRequired'));
      return;
    }
    if (visibility === 'internal' && (externalStatus || externalUrl.trim())) {
      setStepError(t('gest:affichages.form.diffusionInternalConflict'));
      return;
    }
    if (visibility !== 'internal' && !externalStatus) {
      setStepError(t('gest:affichages.form.externalStatusRequired'));
      return;
    }
    if (externalStatus === 'published' && !externalUrl.trim()) {
      setStepError(t('gest:affichages.form.externalUrlRequired'));
      return;
    }
    const count = Number(positionsCount);
    if (!Number.isInteger(count) || count < 1 || count > 99) {
      setStepError(t('gest:affichages.form.positionsCountInvalid'));
      return;
    }
    setStepError(null);
    create.mutate(
      {
        teamId: selectedTeam,
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        // Lignes vides ignorees, plafond 3 (M-300 — le serveur re-valide).
        tasks: tasks.map((task) => task.trim()).filter((task) => task.length > 0).slice(0, 3),
        deadline: deadline || null,
        visibility,
        externalStatus: visibility === 'internal' ? null : (externalStatus || null),
        externalUrl: visibility === 'internal' || externalStatus !== 'published'
          ? null
          : (externalUrl.trim() || null),
        positionsCount: count,
      },
      {
        onSuccess: () => close(),
        onError: (err) => {
          close();
          onError(postingErrorCode(err));
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('gest:affichages.form.title')}
    >
      <div className="flex max-h-full w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">
            {t('gest:affichages.form.title')}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label={t('gest:affichages.form.cancel')}
            className="touch-manipulation rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-posting-team" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.team')}
          </label>
          {singleTeam ? (
            <p className="text-field text-slate-700 dark:text-slate-200">{singleTeam.name}</p>
          ) : (
            <select
              id="new-posting-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className={`${GS_COMPONENTS.input}`}
            >
              <option value="" disabled>
                {t('gest:affichages.form.teamPlaceholder')}
              </option>
              {writableTeams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-posting-title" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.postTitle')}
          </label>
          <input
            id="new-posting-title"
            autoFocus
            maxLength={200}
            placeholder={t('gest:affichages.form.postTitlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-posting-positions" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.positionsCount')}
          </label>
          <input
            id="new-posting-positions"
            type="number"
            min={1}
            max={99}
            value={positionsCount}
            onChange={(e) => setPositionsCount(Number(e.target.value))}
            className={`${GS_COMPONENTS.input} w-32`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-posting-visibility" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.visibility')}
          </label>
          <select
            id="new-posting-visibility"
            value={visibility}
            onChange={(e) => {
              const v = e.target.value as PostingVisibility;
              setVisibility(v);
              if (v === 'internal') {
                setExternalStatus('');
                setExternalUrl('');
              }
            }}
            className={`${GS_COMPONENTS.input}`}
          >
            <option value="internal">{t('gest:affichages.diffusion.internal')}</option>
            <option value="external">{t('gest:affichages.diffusion.external')}</option>
            <option value="both">{t('gest:affichages.diffusion.both')}</option>
          </select>
        </div>

        {visibility !== 'internal' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="new-posting-external-status" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:affichages.form.externalStatus')}
            </label>
            <select
              id="new-posting-external-status"
              value={externalStatus}
              onChange={(e) => {
                const v = e.target.value as PostingExternalStatus | '';
                setExternalStatus(v);
                if (v !== 'published') setExternalUrl('');
              }}
              className={`${GS_COMPONENTS.input}`}
            >
              <option value="" disabled>
                {t('gest:affichages.form.externalStatusPlaceholder')}
              </option>
              <option value="help_needed">{t('gest:affichages.diffusion.helpNeeded')}</option>
              <option value="published">{t('gest:affichages.diffusion.published')}</option>
            </select>
          </div>
        ) : null}

        {visibility !== 'internal' && externalStatus === 'published' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="new-posting-external-url" className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:affichages.form.externalUrl')}
            </label>
            <input
              id="new-posting-external-url"
              type="url"
              maxLength={500}
              placeholder={t('gest:affichages.form.externalUrlPlaceholder')}
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              className={`${GS_COMPONENTS.input}`}
            />
            <p className="text-meta text-slate-400 dark:text-slate-500">
              {t('gest:affichages.form.externalUrlHelp')}
            </p>
          </div>
        ) : null}

        {visibility !== 'internal' && externalStatus === 'help_needed' ? (
          <p className="text-meta text-slate-400 dark:text-slate-500">
            {t('gest:affichages.form.externalUrlHelp')}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <label htmlFor="new-posting-desc" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.description')}
          </label>
          <textarea
            id="new-posting-desc"
            rows={4}
            maxLength={2000}
            placeholder={t('gest:affichages.form.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>

        <TasksFields tasks={tasks} onChange={setTasks} />

        <div className="flex flex-col gap-2">
          <label htmlFor="new-posting-deadline" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:affichages.form.deadline')}
          </label>
          <input
            id="new-posting-deadline"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className={`${GS_COMPONENTS.input} w-48`}
          />
        </div>

        {/* Dette stockage : ajout de fichier differe (blob Firebase non migre). */}
        <p className="text-meta text-slate-400 dark:text-slate-500">
          {t('gest:affichages.form.attachmentDeferred')}
        </p>

        {stepError ? (
          <p role="alert" className="text-field text-red-600 dark:text-red-400">{stepError}</p>
        ) : null}

        <div className="mt-1 flex items-center justify-end gap-2">
          <button type="button" onClick={close} className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}>
            {t('gest:affichages.form.cancel')}
          </button>
          <button type="button" onClick={submit} disabled={create.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:affichages.form.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
